// client/src/components/messages/CreateGroupDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Box,
  Chip
} from '@mui/material';
import axiosInstance from '../../axiosInstance';

const CreateGroupDialog = ({ open, onClose, onGroupCreated }) => {
  const [groupTitle, setGroupTitle] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (open) {
      fetchUsers();
      fetchCategories();
    }
  }, [open]);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users/community');
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
      alert('Failed to load users. Please try again.');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/categories');
      setCategories(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const handleUserToggle = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleCreateGroup = async () => {
    if (!groupTitle.trim() || selectedUsers.length === 0) {
      alert('Please enter a group name and select at least one participant');
      return;
    }

    if (selectedUsers.length > 19) {
      alert('Maximum 19 other participants allowed (20 total including you)');
      return;
    }

    try {
      const response = await axiosInstance.post('/messages/threads', {
        title: groupTitle.trim(),
        participantIds: selectedUsers,
        categoryId: selectedCategory || null,
        isGroup: true,
        context: { type: 'collection' }
      });

      onGroupCreated(response.data);
      handleClose();
    } catch (err) {
      console.error('Error creating group:', err);
      alert(err.response?.data?.error || 'Failed to create group');
    }
  };

  const handleClose = () => {
    setGroupTitle('');
    setSelectedCategory('');
    setSelectedUsers([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Group Conversation</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Group Name"
          fullWidth
          variant="outlined"
          value={groupTitle}
          onChange={(e) => setGroupTitle(e.target.value)}
          placeholder="e.g., Retro Gaming Enthusiasts"
          helperText="Give your group a descriptive name"
          sx={{ mb: 2, mt: 1 }}
        />

        <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
          <InputLabel>Category (Optional)</InputLabel>
          <Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            label="Category (Optional)"
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat._id} value={cat._id}>
                {cat.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>Select Participants (Max 19)</InputLabel>
          <Select
            multiple
            value={selectedUsers}
            onChange={(e) => setSelectedUsers(e.target.value)}
            input={<OutlinedInput label="Select Participants (Max 19)" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((userId) => {
                  const user = users.find(u => u._id === userId);
                  return (
                    <Chip key={userId} label={user?.username || userId} size="small" />
                  );
                })}
              </Box>
            )}
          >
            {users.map((user) => (
              <MenuItem key={user._id} value={user._id}>
                <Checkbox checked={selectedUsers.includes(user._id)} />
                <ListItemText primary={user.username} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ mt: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
          Selected: {selectedUsers.length} / 19 participants
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={handleCreateGroup} 
          variant="contained" 
          disabled={!groupTitle.trim() || selectedUsers.length === 0 || selectedUsers.length > 19}
        >
          Create Group
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateGroupDialog;
